/**
 * Calendar cards in chat (MHS-969): a tool's card data (`widget` on its result, saved on the reply as
 * `messageMetadata.widgets`), read safely, and the math the time grid needs. Times are local wall-clock times in the
 * person's time zone ("2026-11-02T14:00:00", no offset), shown as they are.
 */

import type { SrcSchemasChatMessageResponse } from "@/api";

type Metadata = SrcSchemasChatMessageResponse["messageMetadata"];

export interface BusyBlock {
  start: string;
  end: string;
  /** busy, tentative, oof, workingElsewhere */
  status: string;
  /** What it is, only where that person's calendar sharing lets Outlook say. */
  subject?: string | null;
  location?: string | null;
}

export interface WorkingHours {
  days: string[];
  start: string;
  end: string;
}

export interface CalendarPerson {
  email: string;
  busy: BusyBlock[];
  hours: WorkingHours;
  /** Outlook couldn't read this calendar (e.g. someone outside the company). */
  error?: string | null;
}

export interface Slot {
  start: string;
  end: string;
  everyoneFree: boolean;
  /** Who's busy or tentative then, when no time suits everyone. */
  busy: string[];
  tentative: string[];
}

export interface Window {
  start: string;
  end: string;
}

/** Times that suit everyone, to pick one from (find_meeting_times). */
export interface MeetingPicker {
  type: "meeting_picker";
  id: string;
  timeZone: string;
  window: Window;
  durationMinutes: number;
  subject: string | null;
  organizer: string | null;
  attendees: string[];
  /** Attendees' names, where Anthm knows them (email to name). */
  names: Record<string, string>;
  people: CalendarPerson[];
  slots: Slot[];
}

export interface FreeSummary {
  days: { date: string; freeMinutes: number; workingMinutes: number }[];
  freeMinutes: number;
  workingMinutes: number;
  longestFree: Window | null;
  busiestDay: string | null;
}

/** How someone's week looks (view_schedule). */
export interface CalendarWeek {
  type: "calendar_week";
  id: string;
  timeZone: string;
  window: Window;
  person: CalendarPerson;
  summary: FreeSummary;
}

export type CalendarWidget = MeetingPicker | CalendarWeek;

/** Everyone's day around a meeting waiting for approval (the approval card's preview). */
export interface MeetingDay {
  date: string;
  timeZone: string;
  people: CalendarPerson[];
}

const record = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
const text = (value: unknown): string | null => (typeof value === "string" && value ? value : null);
const strings = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((v): v is string => typeof v === "string") : [];
const num = (value: unknown, fallback = 0) => (typeof value === "number" ? value : fallback);

function windowOf(raw: unknown): Window | null {
  const w = record(raw);
  return text(w.start) && text(w.end) ? { start: String(w.start), end: String(w.end) } : null;
}

function blockOf(raw: unknown): BusyBlock | null {
  const b = record(raw);
  const start = text(b.start);
  const end = text(b.end);
  if (!start || !end) return null;
  return { start, end, status: text(b.status) ?? "busy", subject: text(b.subject), location: text(b.location) };
}

export function personOf(raw: unknown): CalendarPerson | null {
  const p = record(raw);
  const email = text(p.email);
  if (!email) return null;
  const hours = record(p.hours);
  return {
    email,
    busy: (Array.isArray(p.busy) ? p.busy : []).flatMap((b) => blockOf(b) ?? []),
    hours: {
      days: strings(hours.days).length ? strings(hours.days) : ["monday", "tuesday", "wednesday", "thursday", "friday"],
      start: text(hours.start) ?? "09:00",
      end: text(hours.end) ?? "17:00",
    },
    error: text(p.error),
  };
}

export const peopleOf = (raw: unknown): CalendarPerson[] =>
  (Array.isArray(raw) ? raw : []).flatMap((p) => personOf(p) ?? []);

export function slotOf(raw: unknown): Slot | null {
  const s = record(raw);
  const start = text(s.start);
  const end = text(s.end);
  if (!start || !end) return null;
  const everyoneFree = s.everyone_free ?? s.everyoneFree;
  return { start, end, everyoneFree: everyoneFree === true, busy: strings(s.busy), tentative: strings(s.tentative) };
}

function summaryOf(raw: unknown): FreeSummary {
  const s = record(raw);
  return {
    days: (Array.isArray(s.days) ? s.days : []).flatMap((d) => {
      const day = record(d);
      return text(day.date)
        ? [{ date: String(day.date), freeMinutes: num(day.free_minutes), workingMinutes: num(day.working_minutes) }]
        : [];
    }),
    freeMinutes: num(s.free_minutes),
    workingMinutes: num(s.working_minutes),
    longestFree: windowOf(s.longest_free),
    busiestDay: text(s.busiest_day),
  };
}

/** One tool's card, or null when it isn't one this client shows. */
export function widgetOf(raw: unknown, id?: string): CalendarWidget | null {
  const w = record(raw);
  const window = windowOf(w.window);
  const widgetId = id ?? text(w.id);
  if (!window || !widgetId) return null;
  const timeZone = text(w.time_zone) ?? "UTC";
  if (w.type === "meeting_picker") {
    return {
      type: "meeting_picker",
      id: widgetId,
      timeZone,
      window,
      durationMinutes: num(w.duration_minutes, 30),
      subject: text(w.subject),
      organizer: text(w.organizer),
      attendees: strings(w.attendees),
      names: Object.fromEntries(
        Object.entries(record(w.names)).filter((entry): entry is [string, string] => typeof entry[1] === "string"),
      ),
      people: peopleOf(w.people),
      slots: (Array.isArray(w.slots) ? w.slots : []).flatMap((s) => slotOf(s) ?? []),
    };
  }
  if (w.type === "calendar_week") {
    const person = personOf(w.person);
    return person
      ? { type: "calendar_week", id: widgetId, timeZone, window, person, summary: summaryOf(w.summary) }
      : null;
  }
  return null;
}

/** The cards a saved reply's tools gave it. */
export function widgetsFromMetadata(metadata: Metadata): CalendarWidget[] {
  const raw = metadata?.widgets;
  return (Array.isArray(raw) ? raw : []).flatMap((w) => widgetOf(w) ?? []);
}

export function meetingDayOf(raw: unknown): MeetingDay | null {
  const d = record(raw);
  const date = text(d.date);
  return date ? { date, timeZone: text(d.time_zone) ?? "UTC", people: peopleOf(d.people) } : null;
}

// ---------------------------------------------------------------------------
// Time math (wall-clock times, so no time zone conversion)
// ---------------------------------------------------------------------------

/** A local time as a Date at that wall-clock time, for formatting. */
export function wallClock(iso: string): Date {
  const [date, time = "00:00"] = iso.split("T");
  const [y, m, d] = date.split("-").map(Number);
  const [h, min] = time.split(":").map(Number);
  return new Date(y, m - 1, d, h, min);
}

export const dateOf = (iso: string) => iso.slice(0, 10);

/** Minutes since midnight. */
export function minutesOf(iso: string): number {
  const [h, m] = (iso.split("T")[1] ?? "00:00").split(":").map(Number);
  return h * 60 + m;
}

/** "monday", as Outlook names working days. */
const weekdayOf = (day: Date) => day.toLocaleDateString("en-US", { weekday: "long" }).toLowerCase();

/** The days from *window* to show: working days, plus any other day something happens on. */
export function daysIn(window: Window, hours: WorkingHours, busyDates: Iterable<string> = []): string[] {
  const extra = new Set(busyDates);
  const out: string[] = [];
  const day = wallClock(`${dateOf(window.start)}T00:00`);
  const last = wallClock(window.end);
  while (day < last && out.length < 31) {
    const iso = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, "0")}-${String(day.getDate()).padStart(2, "0")}`;
    if (hours.days.includes(weekdayOf(day)) || extra.has(iso)) out.push(iso);
    day.setDate(day.getDate() + 1);
  }
  return out;
}

const hourOf = (hhmm: string) => Number(hhmm.split(":")[0]);

/** The hours to show: working hours, widened to fit anything outside them (whole hours). */
export function hourRange(hours: WorkingHours, times: { start: string; end: string }[]): [number, number] {
  let lo = hourOf(hours.start);
  let hi = Math.max(hourOf(hours.end) + (hours.end.endsWith(":00") ? 0 : 1), lo + 1);
  for (const t of times) {
    if (dateOf(t.start) !== dateOf(t.end)) continue;
    lo = Math.min(lo, Math.floor(minutesOf(t.start) / 60));
    hi = Math.max(hi, Math.ceil(minutesOf(t.end) / 60));
  }
  return [Math.max(0, lo), Math.min(24, hi)];
}

/** The part of *block* on *date*, in minutes since midnight (a block can span days). */
export function onDay(block: { start: string; end: string }, date: string): [number, number] | null {
  const startDate = dateOf(block.start);
  const endDate = dateOf(block.end);
  if (date < startDate || date > endDate) return null;
  const from = startDate === date ? minutesOf(block.start) : 0;
  const to = endDate === date ? minutesOf(block.end) : 24 * 60;
  return to > from ? [from, to] : null;
}

/** Busy time, as on a calendar: busy, out of office and working elsewhere block; tentative doesn't. */
export const isHard = (status: string) => status !== "tentative";
